import { useAppStore } from "@hooks/useAppStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type {
	AppConfigMasteryLevelsType,
	SessionConfigFiltersType,
} from "@memorang/constants/config/schema";
import { isProficiencyTag, isQuestionTypeTag } from "@memorang/helpers/tag";
import type { MixedItemTag } from "@memorang/types";
import {
	getSkillIcon,
	questionTypeIconMap,
	skillIconMap,
} from "@memorang/ui/constants";
import { useProficiencyIconMap } from "@memorang/ui/hooks/useProficiencyIconMap";
import type { LevelIconName } from "@memorang/ui/icon";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { defaultDistributionFilterItems } from "../../session-config/constants/options";
import {
	selectKnownLevelTestsForSkill,
	selectRecommendedTestsForSkill,
} from "../helpers/recommended-tests";
import {
	type SkillDetailEntry,
	legacyTaskToSkillDetailEntry,
	practiceOptionToSkillDetailEntry,
	testToSkillDetailEntry,
} from "../helpers/skill-detail-entries";
import { buildFrontendRecommendationOptions } from "../recommendations";
import {
	type HydratedRecommendationState,
	selectScopedStudyPresentation,
	useHydratedStudyRecommendations,
} from "../recommendations/hooks/useHydratedStudyRecommendations";
import { buildRecommendedSkillTasks } from "./useRecommendedTasksSection/helpers";
import { useRecommendedTestsForSkill } from "./useRecommendedTestsForSkill";

const MAX_KNOWN_LEVEL_ENTRIES = 3;

/**
 * Practice lane: the hydrated engine's skill-scoped sessions when ready,
 * else the legacy single-skill decision tree while the engine falls back.
 */
export const selectPracticeEntriesForSkill = ({
	hydrated,
	skill,
	distributionTags,
	proficiencyIconMap,
	proficiencyLevels,
	distributionFilterItems,
	crossTagSelection,
	t,
}: {
	hydrated: HydratedRecommendationState;
	skill: MixedItemTag;
	distributionTags: MixedItemTag[];
	proficiencyIconMap: Record<string, LevelIconName>;
	proficiencyLevels?: AppConfigMasteryLevelsType;
	distributionFilterItems?: SessionConfigFiltersType;
	crossTagSelection?: boolean;
	t: TFunction;
}): SkillDetailEntry[] => {
	const scoped = selectScopedStudyPresentation({
		state: hydrated,
		filterValueIds: [skill.id],
		sessionCount: 3,
	});
	if (scoped && hydrated.status === "READY") {
		return buildFrontendRecommendationOptions({
			result: scoped,
			skills: hydrated.frontend.skills,
			proficiencies: hydrated.frontend.proficiencies,
			questionTypes: hydrated.frontend.questionTypes,
			translate: t,
			proficiencyIconMap,
			skillIconMap,
			questionTypeIconMap,
		}).map(practiceOptionToSkillDetailEntry);
	}
	if (hydrated.status === "FALLBACK") {
		return buildRecommendedSkillTasks({
			// One-skill universe: the legacy engine cannot leak other skills.
			skills: [skill],
			proficiencies: distributionTags.filter(isProficiencyTag),
			questionTypes: distributionTags.filter(isQuestionTypeTag),
			proficiencyLevels,
			distributionFilterItems,
			crossTagSelection,
			t,
		})
			.map((task, index) => legacyTaskToSkillDetailEntry(task, index, t))
			.filter((entry): entry is SkillDetailEntry => Boolean(entry));
	}
	return [];
};

/**
 * Unknown mode shows a single decision (test if one exists, else one practice
 * option); known mode lists tests then practice options, capped for the dialog.
 */
export const selectSkillDetailEntries = ({
	testEntries,
	practiceEntries,
	levelKnown,
}: {
	testEntries: SkillDetailEntry[];
	practiceEntries: SkillDetailEntry[];
	levelKnown: boolean;
}): SkillDetailEntry[] => {
	if (!levelKnown) {
		return testEntries.length > 0 ? testEntries : practiceEntries.slice(0, 1);
	}
	return [...testEntries, ...practiceEntries].slice(0, MAX_KNOWN_LEVEL_ENTRIES);
};

export const useSkillRecommendations = ({
	skill,
	levelKnown,
}: {
	skill: MixedItemTag | undefined;
	levelKnown: boolean;
}) => {
	const { t } = useTranslation();
	// Cached query (same key as the surfaces that open this dialog) — the
	// legacy fallback lane needs the real proficiency/question-type tags.
	const { data: commonDistribution } = useMixedItemDist();
	const { startTest, starting, exams, examsLoading } =
		useRecommendedTestsForSkill();
	const hydrated = useHydratedStudyRecommendations({
		sessionCount: 3,
		durationMinutes: 10,
	});
	const proficiencyIconMap = useProficiencyIconMap();
	const proficiencyLevels = useAppStore((state) =>
		state.app.proficiencyLevels?.sort((a, b) => a.sortOrder - b.sortOrder),
	);
	const distributionFilterItems = useAppStore(
		(state) =>
			state.app.sessionConfig?.filters || defaultDistributionFilterItems,
	);
	const crossTagSelection = useAppStore(
		(state) => state.app.sessionConfig?.crossTagSelection,
	);

	const entries = useMemo((): SkillDetailEntry[] => {
		if (!skill) {
			return [];
		}
		const distributionTags = commonDistribution?.tags ?? [];
		const skillIcon = getSkillIcon(skill.title);
		const mode = levelKnown ? ("known" as const) : ("unknown" as const);

		const testEntries = (
			levelKnown
				? selectKnownLevelTestsForSkill({ exams, skill })
				: selectRecommendedTestsForSkill({ exams, skill }).slice(0, 1)
		).map(({ kind, exam }) =>
			testToSkillDetailEntry({ kind, exam, skillIcon, mode, t }),
		);

		const practiceEntries = selectPracticeEntriesForSkill({
			hydrated,
			skill,
			distributionTags,
			proficiencyIconMap,
			proficiencyLevels,
			distributionFilterItems,
			crossTagSelection,
			t,
		});

		return selectSkillDetailEntries({
			testEntries,
			practiceEntries,
			levelKnown,
		});
	}, [
		skill,
		levelKnown,
		exams,
		hydrated,
		commonDistribution,
		t,
		proficiencyIconMap,
		proficiencyLevels,
		distributionFilterItems,
		crossTagSelection,
	]);

	return {
		entries,
		isLoading: examsLoading || hydrated.status === "LOADING",
		startTest,
		startingTest: starting,
	};
};
